import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, ChannelBadge } from './inboxIcons';
import InboxOrder from './InboxOrder';

const STATUS_LABEL = { new: 'New', open: 'In progress', waiting: 'Waiting', resolved: 'Resolved' };

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const monthYear = (msVal) => (msVal ? new Date(msVal).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : null);


function ageText(msVal) {
  if (!msVal) return '';
  const h = Math.floor((Date.now() - msVal) / 3600e3);
  return h < 24 ? `${Math.max(h, 0)}h ago` : `${Math.floor(h / 24)}d ago`;
}

// A panel section that folds; closed sections show a one-line summary
function FoldSection({ open, onToggle, title, hint, badge, badgeTone, children }) {
  return (
    <div className="cx-fold">
      <button className="cx-fold-h" onClick={onToggle} aria-expanded={open}>
        <span className="cx-fold-title">{title}</span>
        <span className="cx-fold-hint">{hint}</span>
        {badge != null && badge !== '' && <span className={`cx-chip ${badgeTone || 'cx-c-plain'}`}>{badge}</span>}
        <Icon name="down" size={16} className={open ? 'cx-rot180' : ''} />
      </button>
      {open && <div className="cx-fold-b">{children}</div>}
    </div>
  );
}

// Search box to tie this conversation to a known customer
function LinkPicker({ api, ticketId, onDone, onCancel }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); return undefined; }
    const t = setTimeout(() => {
      api(`/api/v1/support/customer-profiles?q=${encodeURIComponent(term)}`).then((r) => setResults(r.customers || [])).catch((e) => setErr(e.message));
    }, 250);
    return () => clearTimeout(t);
  }, [api, q]);

  const link = async (customerId) => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/v1/support/inbox/${ticketId}/link`, 'POST', { customerId });
      onDone('Linked. Their channels now show together.');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cx-linkpick">
      <label className="cx-search"><Icon name="search" size={15} /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, phone or @handle" aria-label="Find customer" /></label>
      {err && <span className="cx-err">{err}</span>}
      {results && (results.length === 0 ? <span className="cx-hint">No match. Customers appear here once they have written to us.</span> : results.map((c) => (
        <button key={c.id} className="cx-linkpick-row" onClick={() => link(c.id)} disabled={busy}>
          <b>{c.name}</b>
          <span>{c.identities.map((i) => i.value).join(' · ') || 'No contact details'}</span>
        </button>
      )))}
      <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={onCancel}>Cancel</button>
    </div>
  );
}

// Customer 360: who they are across channels, orders, cases, open cart, AI summary
// cartId: the panel for an abandoned cart opened from the list — read-only, no conversation behind it
function InboxCustomer({ api, ticketId, cartId = null, header, onClose, onOpenCart, onOpenConversation, onLinked, toast, briefKey = 0 }) {
  const panelFor = cartId ? `cart:${cartId}` : ticketId;
  const source = cartId ? `/api/v1/support/carts/${encodeURIComponent(cartId)}` : `/api/v1/support/inbox/${ticketId}`;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [folds, setFolds] = useState(() => { try { return JSON.parse(localStorage.getItem('cx-folds') || '{}'); } catch { return {}; } });
  const [briefDone, setBriefDone] = useState({});

  // A different conversation: start fresh (loading bars). Same conversation: refresh in place, no blanking.
  const shownFor = useRef(null);
  useEffect(() => {
    let alive = true;
    if (shownFor.current !== panelFor) {
      setData(null);
      setBriefDone({});
      setPicking(false);
    }
    setError(null);
    api(`${source}/customer`)
      .then((r) => { if (alive) { setData(r.customer); shownFor.current = panelFor; } })
      .catch((err) => { if (alive) setError(err.message); });
    return () => { alive = false; };
  }, [api, panelFor, source, reload]);

  // After an action elsewhere (send, status, assign…), rebuild only the brief
  const dataRef = useRef(null);
  dataRef.current = data;
  const refreshBrief = useCallback(async (orderNumber) => {
    const d = dataRef.current;
    if (!d || d.readOnly) return;
    const order = orderNumber ?? (d.orderCard?.pinned || d.orderCard?.selected || '');
    try {
      const r = await api(`/api/v1/support/inbox/${ticketId}/brief?order=${encodeURIComponent(order || '')}&orders=${d.stats?.orders || 0}&since=${d.sinceMs || ''}`);
      setData((p) => (p ? { ...p, brief: r.brief } : p));
      setBriefDone({});
    } catch {
      // Keep the current brief if the refresh fails
    }
  }, [api, ticketId]);
  const seenBriefKey = useRef(briefKey);
  useEffect(() => {
    if (seenBriefKey.current === briefKey) return;
    seenBriefKey.current = briefKey;
    refreshBrief();
  }, [briefKey, refreshBrief]);

  // Order pinned or verified: update the card here and refresh the brief, nothing else
  const onPinned = (number, detail) => {
    setData((p) => {
      if (!p?.orderCard) return p;
      const c = p.orderCard;
      return {
        ...p,
        orderCard: {
          ...c,
          pinned: number,
          pinnedByName: number ? 'you' : null,
          selected: number || c.selected,
          reason: number ? 'pinned' : c.reason,
          tabs: number && !c.tabs.includes(number) ? [number, ...c.tabs] : c.tabs,
          details: detail && number ? { ...c.details, [number]: detail } : c.details,
        },
      };
    });
    refreshBrief(number || undefined);
    onLinked?.();
  };
  const onVerified = (number, detail) => {
    setData((p) => (p?.orderCard ? { ...p, orderCard: { ...p.orderCard, details: { ...p.orderCard.details, [number]: detail } } } : p));
    refreshBrief(number);
  };

  const changed = (msg) => { setPicking(false); setReload((n) => n + 1); onLinked?.(); if (msg) toast?.(msg); };
  const act = async (path, msg) => {
    setBusy(true);
    try {
      await api(`/api/v1/support/inbox/${ticketId}/${path}`, 'POST');
      changed(msg);
    } catch (e) {
      toast?.(e.message, true);
    } finally {
      setBusy(false);
    }
  };

  if (error) return <aside className="cx-ctx">{header}<div className="cx-empty"><strong>Couldn't load customer</strong>{error}</div></aside>;
  if (!data) return <aside className="cx-ctx" aria-busy="true">{header}<div className="cx-skel" /><div className="cx-skel" /><div className="cx-skel" /></aside>;

  const o = data.latestOrder;
  const card = data.orderCard;
  const cardDetail = card?.selected ? card.details?.[card.selected] : null;
  const brief = data.brief;
  const openCases = data.cases.filter((c) => c.status !== 'resolved').length;
  const fold = (id, def) => (folds[id] ?? def);
  const toggle = (id, def) => setFolds((p) => {
    const next = { ...p, [id]: !(p[id] ?? def) };
    try { localStorage.setItem('cx-folds', JSON.stringify(next)); } catch { /* storage unavailable */ }
    return next;
  });
  const tick = async (step) => {
    setBriefDone((p) => ({ ...p, [step.key]: !(p[step.key] ?? step.done) }));
    try {
      await api(`/api/v1/support/inbox/${ticketId}/brief-step`, 'POST', { key: step.key, done: !(briefDone[step.key] ?? step.done) });
    } catch (e) {
      toast?.(e.message, true);
    }
  };


  return (
    <aside className="cx-ctx" aria-label="Customer details">
      {header}
      {!header && <div className="cx-ctx-closebar"><button className="cx-btn cx-btn-icon cx-btn-ghost cx-ctx-close" onClick={onClose} aria-label="Close customer details"><Icon name="x" /></button></div>}
      {data.linkSuggestion && (
        <div className="cx-sec cx-suggest-wrap">
         <div className="cx-suggest" role="group" aria-label="Same customer?">
          <div className="cx-suggest-h"><span className="cx-suggest-ic"><Icon name="link" size={14} /></span>Same customer?</div>
          <div className="cx-suggest-body">Looks like <b>{data.linkSuggestion.name}</b>. {data.linkSuggestion.reason}.</div>
          <div className="cx-suggest-actions">
            <button className="cx-btn cx-btn-primary cx-btn-sm" disabled={busy} onClick={async () => {
              setBusy(true);
              try {
                await api(`/api/v1/support/inbox/${ticketId}/link`, 'POST', { customerId: data.linkSuggestion.customerId });
                changed(`Linked to ${data.linkSuggestion.name}`);
              } catch (e) { toast?.(e.message, true); } finally { setBusy(false); }
            }}>Same person, link</button>
            <button className="cx-btn cx-btn-ghost cx-btn-sm" disabled={busy} onClick={() => act('link-suggestion/dismiss', 'Suggestion dismissed')}>Not them</button>
          </div>
         </div>
        </div>
      )}

      {brief && (brief.wants || brief.facts || brief.steps.length > 0) && (
        <div className="cx-sec cx-brief" aria-label="Brief">
          <h3><span className="cx-brief-title"><Icon name="spark" size={14} />Brief</span>{brief.mood && <span className="cx-chip cx-c-new">{brief.mood}</span>}</h3>
          <dl className="cx-brief-grid">
            {brief.wants && <><dt>Wants</dt><dd>{brief.wants}</dd></>}
            {brief.facts && <><dt>Facts</dt><dd>{brief.facts}</dd></>}
            {brief.history && <><dt>History</dt><dd>{brief.history}</dd></>}
          </dl>
          {brief.steps.length > 0 && (
            <div className="cx-brief-steps">
              <span>Next steps</span>
              {brief.steps.map((st) => {
                const done = briefDone[st.key] ?? st.done;
                return (
                  <label key={st.key} className={`cx-step${done ? ' done' : ''}`}>
                    <input type="checkbox" className="cx-check" checked={done} onChange={() => tick(st)} />
                    <span>{st.text}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {card ? (
        <FoldSection
          open={fold(cartId ? 'cartorder' : 'order', !cartId)}
          onToggle={() => toggle(cartId ? 'cartorder' : 'order', !cartId)}
          title={cartId ? 'Latest order' : 'Order in question'}
          hint={cardDetail?.number ? `#${cardDetail.number}` : card.reason === 'none' ? 'None mentioned' : ''}
          badge={cardDetail?.rto ? (cardDetail.rtoLabel === 'Returned to us' ? 'Returned' : 'Returning') : cardDetail?.late ? 'Late' : cardDetail?.cancelled ? 'Cancelled' : cardDetail?.verified === false ? 'Not verified' : ''}
          badgeTone={cardDetail?.rto || cardDetail?.late ? 'cx-c-new' : cardDetail?.cancelled ? 'cx-c-plain' : 'cx-c-waiting'}
        >
          <InboxOrder bare api={api} ticketId={panelFor} card={card} toast={toast} onPinned={onPinned} onVerified={onVerified} base={cartId ? source : null} readOnly={!!data.readOnly} />
        </FoldSection>
      ) : o ? (
        <FoldSection open={fold('order', true)} onToggle={() => toggle('order', true)} title="Latest order" hint={`#${o.number}`}>
          <p className="cx-summary">#{o.number} · {o.eta}</p>
        </FoldSection>
      ) : null}

      <FoldSection
        open={fold('customer', !!cartId)}
        onToggle={() => toggle('customer', !!cartId)}
        title="Customer"
        hint={[data.city, `${data.stats.orders} order${data.stats.orders === 1 ? '' : 's'}`, inr(data.stats.spent)].filter(Boolean).join(' · ')}
      >
        <div className="cx-since">{[data.city, data.sinceMs ? `Customer since ${monthYear(data.sinceMs)}` : 'No orders yet'].filter(Boolean).join(' · ')}</div>
        <div className="cx-ids">
          {data.identities.map((id) => (
            <div key={id.type + id.value}>
              {id.type === 'phone' ? <span className="cx-ch cx-ch-plain"><Icon name="phone" size={12} /></span> : <ChannelBadge channel={id.type} />}
              <span>{id.value}</span>
            </div>
          ))}
        </div>
        <div className="cx-statline">
          <span><b className="cx-mono">{data.stats.orders}</b> order{data.stats.orders === 1 ? '' : 's'}</span>
          <span><b className="cx-mono">{inr(data.stats.spent)}</b> spent</span>
          <span><b className="cx-mono">{data.stats.openCases}</b> open case{data.stats.openCases === 1 ? '' : 's'}</span>
        </div>
        {data.readOnly ? null : picking ? <LinkPicker api={api} ticketId={ticketId} onDone={changed} onCancel={() => setPicking(false)} /> : (
          <div className="cx-link-actions">
            <button className="cx-linkbtn" onClick={() => setPicking(true)}><Icon name="link" size={13} />Link to another customer</button>
            {data.otherConversations?.length > 0 && <button className="cx-linkbtn" disabled={busy} onClick={() => act('unlink', 'Unlinked. This conversation now stands alone.')}>Not the same person? Unlink</button>}
          </div>
        )}
      </FoldSection>

      {data.cases.length > 0 && (
        <FoldSection open={fold('cases', false)} onToggle={() => toggle('cases', false)} title="Cases" hint={data.cases[0]?.title} badge={openCases ? `${openCases} open` : `${data.cases.length}`} badgeTone={openCases ? 'cx-c-new' : 'cx-c-plain'}>
          {data.cases.map((c) => (
            <div className="cx-case" key={c.id}>
              <span className="cx-mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{c.ref}</span>
              <span>{c.title}</span>
              <span className={`cx-chip cx-c-${c.status}`}>{STATUS_LABEL[c.status]}</span>
            </div>
          ))}
        </FoldSection>
      )}

      {(data.otherConversations?.length > 0 || data.hiddenConversations > 0) && (
        <FoldSection open={fold('other', false)} onToggle={() => toggle('other', false)} title="Other conversations" hint={(data.otherConversations || []).map((c) => c.channel === 'ig' ? 'Instagram' : c.channel === 'fb' ? 'Messenger' : c.channel === 'form' ? 'Form' : 'Email').join(', ')} badge={`${data.otherConversations.length}`}>
          {data.otherConversations.map((c) => (
            <button key={c.id} className="cx-other" onClick={() => onOpenConversation?.(c.id)}>
              <ChannelBadge channel={c.channel} />
              <span className="cx-other-main">
                <span>{c.ref && <span className="cx-mono">{c.ref} · </span>}{c.title || c.preview || 'Conversation'}</span>
                <small>{ageText(c.lastActivityMs)}</small>
              </span>
              <span className={`cx-chip cx-c-${c.status}`}>{STATUS_LABEL[c.status]}</span>
            </button>
          ))}
          {data.hiddenConversations > 0 && <p className="cx-summary">{data.hiddenConversations} more on channels you don't have access to.</p>}
        </FoldSection>
      )}

      {data.cart && (
        <FoldSection open={fold('cart', true)} onToggle={() => toggle('cart', true)} title="Abandoned cart" hint={`${inr(data.cart.value)} · left ${ageText(data.cart.createdMs)}`} badge="Open" badgeTone="cx-c-waiting">
          <div className="cx-cartcard">
            <Icon name="cart" />
            <span><b>{inr(data.cart.value)}</b> · {data.cart.items} item{data.cart.items === 1 ? '' : 's'} · left {ageText(data.cart.createdMs)}</span>
            {onOpenCart && <button className="cx-btn cx-btn-sm" style={{ marginLeft: 'auto' }} onClick={() => onOpenCart(data.cart.id)}>Open</button>}
          </div>
        </FoldSection>
      )}
    </aside>
  );
}

export default InboxCustomer;
